import { Button, Icon, MenuItem } from "@material-ui/core";
import _ from "lodash";
import CalendarRange from "mdi-material-ui/CalendarRange";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Update from "mdi-material-ui/Update";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { ASSET_FILETYPE, ASSET_RESOLUTION, AssetType } from "common/constants";
import { ActionPermissionsObject, IWorkspaceResourceAsset, PermissionActionType, WorkspacePlanTier, WorkspaceResourceType } from "common/interfaces";
import Banner from "js/Components/Banner";
import { Breadcrumb } from "js/Components/Breadcrumb";
import UpgradeIcon from "js/Components/UpgradeIcon";
import SharedAssetsPaneController, { SharedAssetsPaneControllerState } from "js/controllers/SharedAssetsPaneController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Gap20, Gap30, Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, NoTeamAssetNotice } from "js/react/components/Notice";
import { IconMenu, RoundIconButton, UIPane } from "js/react/components/UiComponents";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import AddAssetsContainer from "js/react/views/AddAssets/AddAssetsContainer";
import { FilterDropDown } from "js/react/views/AddSlide/Panes/Components/FilterDropDown";
import {
    SearchBarContainer,
    SearchBarInnerContainer,
    SlideSearchInput,
    UIPaneResultsContainer,
} from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { SortMenu } from "js/react/views/AddSlide/Panes/Components/SortMenu";
import { ThumbnailCard, thumbnailMenuButtonStyles } from "js/react/views/AddSlide/Panes/Components/ThumbnailCard";
import { MultiSelectThumbnailGrid, Thumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { BulkAssetUploadDialog, BulkSetTagsDialog } from "js/react/views/TeamResources/dialogs/BulkAssetUploadDialog";
import CreateTeamAssetDialog from "js/react/views/TeamResources/dialogs/CreateTeamAssetDialog";
import TeamAssetSettingsDialog from "js/react/views/TeamResources/dialogs/TeamAssetSettingsDialog";
import { loadImage } from "js/core/utilities/promiseHelper";

const logger = getLogger(LogGroup.TEAMS);

const StyledUIPaneResultsContainer = styled(UIPaneResultsContainer) <{ editable?: boolean }>`
     padding-top: 20px;
     margin-top: 0;
`;

const StyledSearchBarContainer = styled(SearchBarContainer)`
    background: #eee;
    position: relative;
`;

const StyledSearchBarInnerContainer = styled(SearchBarInnerContainer) <{ editable?: boolean }>`
    background: ${props => props.editable ? "white" : "#eee"};
`;

const ThumbnailGridContainer = styled.div`
    margin: 0px 0px 20px;
    overflow: hidden;

    .dark-mode & {
        background: #333;
    }
`;

const UploadFileInput = styled.input`
    width: 100%;
    height: 100%;
    opacity: 0;
    position: absolute;

`;

const StyledIconMenu = styled(IconMenu)`
    &&& {
        ${thumbnailMenuButtonStyles}
    }
`;

/** Centered visibility overlay for unpublished/hidden assets (matches TeamSlideThumbnail disabled slide treatment). */
const HiddenAssetIcon = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 30px;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        color: white;
        font-size: 20px;
    }
`;

const ThumbnailCardBar = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: 9px 10px 10px 10px;

    > div {
        line-height: 150%;
        display: flex;
        align-items: center;
        font-size: 14px;
        letter-spacing: 0px;
        font-weight: 600;
        color: #222;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        text-align: left;
        flex-grow: 2;
        text-transform: none;
        margin: 0px;
    }

`;

interface AssetThumbnailProps {
    item: IWorkspaceResourceAsset;
    onMouseDown: (event: React.MouseEvent) => void;
    onMouseUp: (event: React.MouseEvent) => void;
    onDoubleClick: (item: any) => void;
    onContextMenu: (event: React.MouseEvent) => void;
    selected: boolean;
    readOnly: boolean;
    allSelected: IWorkspaceResourceAsset[];
    renderContextMenuItems: (selection: IWorkspaceResourceAsset[]) => React.ReactNode;
    onVisibleChange: (visible: boolean, item: IWorkspaceResourceAsset) => void;
}

const AssetThumbnail = (props: AssetThumbnailProps & { canUpgradeToManageSharedResources?: boolean }) => {
    const {
        item: asset,
        onMouseDown,
        onMouseUp,
        onDoubleClick,
        onContextMenu,
        selected,
        readOnly,
        allSelected,
        renderContextMenuItems,
        canUpgradeToManageSharedResources,
        onVisibleChange
    } = props;

    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
    const [isHovering, setIsHovering] = useState<boolean>(false);

    const ref = useRef<HTMLDivElement>(null);

    const visible = useIntersectionObserver(ref);

    const fetchThumbnailUrl = async () => {
        const currentThumbnailUrl = thumbnailUrl;
        setThumbnailUrl(null);
        try {
            const assetModel = await ds.assets.getAssetById(asset.resourceId);
            if (assetModel.get("actualId") !== asset.metadata.actualId) {
                await assetModel.reload();
            }
            let url = await assetModel.getURL(ASSET_RESOLUTION.XSMALL);
            try {
                await loadImage(url);
            } catch {
                url = await assetModel.getURL(ASSET_RESOLUTION.XSMALL, true);
                await loadImage(url);
            }
            setThumbnailUrl(url);
        } catch (err) {
            logger.error(err, "[TeamAssetsPane] [AssetThumbnail] getThumbnailUrl() failed");
            if (currentThumbnailUrl) {
                setThumbnailUrl(currentThumbnailUrl);
            }
        }
    };

    useEffect(() => {
        onVisibleChange?.(visible, asset);

        if (visible && _.isEmpty(thumbnailUrl)) {
            fetchThumbnailUrl();
        }
    }, [visible]);

    useEffect(() => {
        if (visible) {
            fetchThumbnailUrl();
        }
    }, [asset.metadata.actualId]);

    return (
        <ThumbnailCard
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onDoubleClick={!readOnly ? onDoubleClick : undefined}
            onContextMenu={!readOnly ? onContextMenu : undefined}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            ref={ref}
            selected={selected}
            menuOverlay={isHovering && !readOnly && (
                <StyledIconMenu handleCloseMenu={() => setIsHovering(false)} fontSize="medium">{renderContextMenuItems(allSelected.length === 0 ? [asset] : allSelected)}</StyledIconMenu>
            )}
            barVariant="custom"
            barContent={
                <ThumbnailCardBar><div>{asset.metadata.name}</div></ThumbnailCardBar>
            }

        >
            <Thumbnail
                url={thumbnailUrl}
                readOnly
                style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                imageStyle={!asset.metadata.isPublished ? { filter: "brightness(0.7)" } : {}}
            >
                {!asset.metadata.isPublished && (<HiddenAssetIcon>
                    <Icon>visibility_off</Icon>
                </HiddenAssetIcon>)}
            </Thumbnail>
        </ThumbnailCard>
    );
};

const MEDIA_FILTER_OPTIONS = [
    {
        id: "__all__",
        nameKey: "All Media"
    },
    {
        id: "image",
        nameKey: "Photos"
    },
    {
        id: "logo",
        nameKey: "Logos"
    },
];

const SORT_OPTIONS = [
    {
        value: "resource.metadata.name",
        labelKey: "Name",
        icon: <SortAlphabeticalVariant />
    },
    {
        value: "resource.createdAt",
        labelKey: "Created At",
        reverse: true,
        icon: <CalendarRange />
    },
    {
        value: "resource.modifiedAt",
        labelKey: "Modified At",
        reverse: true,
        icon: <Update />
    },
];

interface TeamAssetsPaneProps {
    prefilledSearchQuery?: string;
    editable?: boolean;
    addAssetCallback?: (asset: any) => void;
    handleConfirm?: () => void;
}

function TeamAssetsPane(props: TeamAssetsPaneProps & WorkspaceControllerState & SharedAssetsPaneControllerState) {
    const {
        editable,
        addAssetCallback,
        handleConfirm,
        workspace,
        data,
        tags,
        sort,
        search,
        filters,
        fetching
    } = props;

    const mediaFilterOptions = MEDIA_FILTER_OPTIONS.map(option => ({ ...option, name: t(option.nameKey) }));
    const sortOptions = SORT_OPTIONS.map(option => ({ ...option, label: t(option.labelKey) }));

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;

    const existingTags = tags.map(tag => tag.tag);

    const handleSearch = async (searchTerm: string) => {
        SharedAssetsPaneController.setSearch({ query: searchTerm?.trim().toLowerCase() });
    };

    const createOrUpdateTeamAsset = async (
        fromAssetId: string,
        attributesOverrides: { tags?: string[], name?: string, type?: string } = {},
        assetResource: IWorkspaceResourceAsset = null,
        isNewAsset: boolean = false,
    ) => {
        if (assetResource) {
            try {
                await WorkspaceController.updateWorkspaceResource({
                    resourceId: assetResource.resourceId,
                    resourceType: WorkspaceResourceType.ASSET,
                    tags: attributesOverrides?.tags ?? assetResource?.tags ?? [],
                    metadata: {
                        type: attributesOverrides?.type ?? assetResource.metadata.type,
                        createdBy: assetResource.metadata?.createdBy,
                        name: attributesOverrides?.name ?? assetResource.metadata.name,
                        alt: assetResource.metadata.alt,
                        isPublished: assetResource.metadata?.isPublished,
                        actualId: fromAssetId
                    }
                }, { isNewAsset, isReplacedAsset: !!assetResource });

                const asset = await ds.assets.getAssetById(assetResource.resourceId);
                await asset.reload();
            } catch (error) {
                logger.error(error, "[TeamAssetsPane] Error updating workspace asset");
            }

            return;
        }

        try {
            const asset = await ds.assets.cloneAsset(fromAssetId, {
                userId: null,
                id: `${workspace.id}:${uuid()}`
            }, false);

            if (!asset || !asset.id) {
                throw new Error("Failed to clone asset: asset is missing or invalid.");
            }

            await WorkspaceController.createWorkspaceResource({
                resource: {
                    resourceId: asset.id,
                    resourceType: WorkspaceResourceType.ASSET,
                    folderIds: [],
                    tags: attributesOverrides?.tags || [],
                    metadata: {
                        type: attributesOverrides?.type ?? asset.get("type"),
                        createdBy: app.user.id,
                        modifiedBy: app.user.id,
                        name: attributesOverrides?.name ?? asset.get("assetName"),
                        alt: asset.get("name") ?? "",
                        isPublished: true,
                        actualId: fromAssetId
                    }
                },
                defaultPermissions: "shared"
            }, { isNewAsset, assetType: attributesOverrides?.type ?? asset.get("type") }) as IWorkspaceResourceAsset;

            await asset.reload();
        } catch (error) {
            logger.error(error, "[TeamAssetsPane] Error cloning and creating workspace asset");
        }
    };

    const handleAddItem = async () => {
        const { createdAssetModel, isNewAsset } = await new Promise<{ createdAssetModel: { content_type: string, content_value: string, assetName: string, tags: string[] }, isNewAsset: boolean }>(resolve => {
            ShowDialog(AddAssetsContainer, {
                assetType: AssetType.IMAGE,
                hideTeamAssets: true,
                hideIcons: true,
                hideVideos: true,
                callback: (model: { content_type: string, content_value: string, assetName: string, tags: string[] }, isNew: boolean) => {
                    resolve({ createdAssetModel: model, isNewAsset: isNew });
                }
            });
        });

        if (!createdAssetModel) {
            return;
        }

        ShowDialog(CreateTeamAssetDialog, {
            type: createdAssetModel.content_type,
            name: createdAssetModel.assetName,
            tags: createdAssetModel.tags,
            existingTags,
            callback: async ({ type, name, tags }) => {
                try {
                    await createOrUpdateTeamAsset(createdAssetModel.content_value, { type, name, tags }, null, isNewAsset);
                } catch (err) {
                    logger.error(err, "[TeamAssetsPane] Error creating team asset", { name });
                    ShowErrorDialog({
                        error: t("An error occurred while creating the team asset."),
                        message: (
                            <>
                                <p>
                                    <strong>{t("Error")}:</strong> {err.message}
                                </p>
                                <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                            </>
                        )
                    });
                }
            }
        });
    };

    const handleUpdateAsset = async (teamAsset: IWorkspaceResourceAsset) => {
        return new Promise(resolve => {
            const showAssetsDialog = () => {
                ShowDialog(AddAssetsContainer, {
                    assetType: teamAsset.metadata.type,
                    hideTeamAssets: true,
                    hideIcons: true,
                    hideVideos: true,
                    hidePhotos: teamAsset.metadata.type !== AssetType.IMAGE,
                    hideLogos: teamAsset.metadata.type !== AssetType.LOGO,
                    onCancel: () => resolve(false),
                    callback: async (model: { content_type: string, content_value: string, assetName: string, tags: string[] }, isNew: boolean) => {
                        try {
                            await createOrUpdateTeamAsset(model.content_value, { type: model.content_type }, teamAsset, isNew);

                            resolve(true);
                        } catch (err) {
                            logger.error(err, "[TeamAssetsPane] Error replacing asset", { id: teamAsset.resourceId });
                            ShowErrorDialog({
                                error: t("An error occurred while updating the asset."),
                                message: (
                                    <>
                                        <p>
                                            <strong>{t("Error")}:</strong> {err.message}
                                        </p>
                                        <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                                    </>
                                )
                            });
                            resolve(false);
                        }
                    }
                });
            };

            ShowConfirmationDialog({
                title: t("Update Asset"),
                message: (
                    <>
                        <p>{t("You can update this asset with a new version and any slides that currently use this asset will be automatically updated.")}</p>
                        <p>{t("This can be useful if you need to update a team logo or headshot or other asset that you'd like to easily replace with a new version across all existing slides that use it.")}</p>
                    </>
                ),
                acceptCallback: showAssetsDialog,
                cancelCallback: () => resolve(false),
            });
        });
    };

    const handleDeleteAssets = async (selection: IWorkspaceResourceAsset[]) => {
        if (await ShowConfirmationDialog({
            title: selection.length === 1
                ? t("Are you sure you want to delete this asset?")
                : t("Are you sure you want to delete {{count}} selected assets?", { count: selection.length }),
            message: t("Slides that already use this asset will not be changed. This action can not be undone."),
            buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
            okButtonLabel: t("Delete"),
        })) {
            for (const asset of selection) {
                try {
                    await withProgressDialog({
                        title: t("Deleting Team Asset..."),
                        action: () => WorkspaceController.deleteWorkspaceResource({ resourceId: asset.resourceId, resourceType: WorkspaceResourceType.ASSET })
                    });
                } catch (err) {
                    logger.error(err, "[TeamAssetsPane]  failed", { resourceId: asset.resourceId, resourceType: WorkspaceResourceType.ASSET });

                    ShowErrorDialog({
                        error: t("An error occurred while deleting the Team Asset."),
                        message: (
                            <>
                                <p>
                                    <strong>{t("Error")}:</strong> {err.message}
                                </p>
                                <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                            </>
                        )
                    });
                }
            }
        }
    };

    const handleEditSettings = async (assetResource: IWorkspaceResourceAsset) => {
        ShowDialog(TeamAssetSettingsDialog, {
            assetResource,
            existingTags
        });
    };

    const handleMediaFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;

        const otherFilters = filters.filter(filter => filter.field !== "resource.metadata.type");
        if (value === "__all__") {
            SharedAssetsPaneController.setFilters([...otherFilters, {
                field: "resource.metadata.type",
                value: ["image", "logo"],
                operator: "in"
            }]);
        } else {
            SharedAssetsPaneController.setFilters([...otherFilters, {
                field: "resource.metadata.type",
                value: value,
                operator: "eq"
            }]);
        }
    };

    const handleTagFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;

        const otherFilters = filters.filter(filter => filter.field !== "resource.tags");
        if (value === "__all__") {
            SharedAssetsPaneController.setFilters(otherFilters);
        } else {
            SharedAssetsPaneController.setFilters([...otherFilters, {
                field: "resource.tags",
                value: value,
                operator: "eq"
            }]);
        }
    };

    const handleSort = ({ field, reverse }: { field: string, reverse: boolean }) => {
        SharedAssetsPaneController.setSort([{ field, direction: reverse ? "desc" : "asc" }]);
    };

    const handleSetTags = (selection: IWorkspaceResourceAsset[]) => {
        ShowDialog(BulkSetTagsDialog, {
            selection,
            existingTags,
            onAccept: async (tags: string[]) => {
                for (const asset of selection) {
                    try {
                        await WorkspaceController.updateWorkspaceResource({
                            ...asset,
                            tags: [...tags]
                        });
                    } catch (err) {
                        logger.error(err, "[TeamAssetsPane] handleSetTags() failed", { id: asset.id });
                    }
                }
            }
        });
    };

    const handleBulkUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files?.length) {
            return;
        }

        ShowDialog(BulkAssetUploadDialog, {
            files,
            existingTags,
            types: AssetType.IMAGE,
            onAccept: async ({ tags, types }: { tags: string[], types: any }) => {
                const progressDialog = ShowDialog(ProgressDialog, {
                    title: t("Uploading Assets..."),
                    progress: 0
                });

                for (let i = 0; i < files.length; i++) {
                    let file = files[i];
                    progressDialog.setProgress(i / files.length * 100);
                    progressDialog.setMessage(t("Uploading {{fileName}}", { fileName: file.name }));

                    let fileType;
                    if (file.type === "image/jpeg") {
                        fileType = ASSET_FILETYPE.JPG;
                    } else if (file.type === "image/png") {
                        fileType = ASSET_FILETYPE.PNG;
                    } else if (file.type === "image/gif") {
                        fileType = ASSET_FILETYPE.GIF;
                    } else if (file.type === "image/svg+xml") {
                        fileType = ASSET_FILETYPE.SVG;
                    }
                    let currentType = AssetType.IMAGE;
                    if (types === "logo") {
                        currentType = AssetType.LOGO;
                    }
                    try {
                        const asset = await ds.assets.getOrCreateImage({
                            file,
                            name: file.name,
                            fileType,
                            assetType: currentType,
                            metadata: {
                                source: "bulk-upload"
                            }
                        });

                        await createOrUpdateTeamAsset(asset.id, { tags, name: file.name, type: currentType }, null);
                    } catch (err) {
                        logger.error(err, "[TeamAssetsPane] Bulk upload failed for file", { name: file.name });
                    }
                }

                progressDialog.props.closeDialog();
            }
        });
    };

    const renderContextMenu = (selection: IWorkspaceResourceAsset[]) => {
        if (canUpgradeToManageSharedResources) {
            // When downgraded, show disabled menu items with upgrade icons (except delete)
            const menuItems = [];
            if (selection.length === 1) {
                menuItems.push(<MenuItem divider key="edit" disabled><UpgradeIcon /><Gap5 />{t("Edit Asset Properties...")}</MenuItem>);
                menuItems.push(<MenuItem divider key="replace" disabled><UpgradeIcon /><Gap5 />{t("Update Asset...")}</MenuItem>);
            } else {
                menuItems.push(<MenuItem divider key="tags" disabled><UpgradeIcon /><Gap5 />{t("Set Tags...")}</MenuItem>);
            }

            // Delete is always functional (not disabled)
            menuItems.push(<MenuItem key="trash" onClick={() => handleDeleteAssets(selection)}>
                <Icon>delete</Icon>{selection.length === 1 ? t("Delete Asset") : t("Delete {{count}} Selected Assets", { count: selection.length })}
            </MenuItem>);

            return menuItems;
        }

        // Normal rendering when not downgraded
        const menuItems = [];
        if (selection.length === 1) {
            menuItems.push(<MenuItem divider key="edit" onClick={() => handleEditSettings(selection[0])}>
                <Icon>edit</Icon>{t("Edit Asset Properties...")}
            </MenuItem>);

            menuItems.push(<MenuItem divider key="replace" onClick={() => handleUpdateAsset(selection[0])}>
                <Icon>sync</Icon>{t("Update Asset...")}
            </MenuItem>);

            menuItems.push(<MenuItem key="trash" onClick={() => handleDeleteAssets(selection)}>
                <Icon>delete</Icon>{t("Delete Asset")}
            </MenuItem>);
        } else {
            menuItems.push(<MenuItem divider key="tags" onClick={() => handleSetTags(selection)}>
                <Icon>label</Icon>{t("Set Tags...")}
            </MenuItem>);
            menuItems.push(<MenuItem key="trash" onClick={() => handleDeleteAssets(selection)}>
                <Icon>delete</Icon>{t("Delete {{count}} Selected Assets", { count: selection.length })}
            </MenuItem>);
        }

        return menuItems;
    };

    const handleThumbnailVisibilityChange = (visible: boolean, item: IWorkspaceResourceAsset) => {
        if (!visible) {
            return;
        }

        if (!SharedAssetsPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(({ resource }) => resource.id === item.id);
        if (index === data.length - 1) {
            SharedAssetsPaneController.loadMore();
        }
    };

    const tagOptions = [
        {
            id: "__all__",
            name: t("All Tags"),
        },
        ...tags.map(({ tag, count }) => {
            return {
                id: tag,
                name: `${tag} (${count})`,
            };
        }),
    ];

    const filteredAssets = data.map(record => record.resource);

    const mediaFilter = filters.find(filter => filter.field === "resource.metadata.type");
    const mediaFilterValue = mediaFilter?.operator === "eq" ? mediaFilter.value : "__all__";
    const tagFilter = filters.find(filter => filter.field === "resource.tags");
    const tagFilterValue = tagFilter?.value ?? "__all__";

    const isLoadingMore = fetching && data.length > 0;
    const isLoadingInitial = fetching && data.length === 0;

    return (
        <UIPane>
            <StyledSearchBarContainer
                className="search-container"
            >
                {editable && canManageSharedResources && <>
                    <Breadcrumb>{t("Team Assets")}</Breadcrumb>
                    <RoundIconButton
                        onClick={() => handleAddItem()}
                    >
                        <Icon>add</Icon>
                    </RoundIconButton>
                    <Gap20 />
                    <Button size="small" color="primary">
                        <Icon>drive_folder_upload</Icon>
                        {t("Bulk Upload...")}
                        <UploadFileInput
                            type="file"
                            multiple
                            accept={"image/png,image/jpeg,image/gif,image/svg+xml"}
                            onChange={handleBulkUpload}
                        />
                    </Button>

                    <Gap30 />
                </>}
                {canUseSharedResources && (
                    <StyledSearchBarInnerContainer editable={editable}>
                        <SlideSearchInput
                            query={search.query}
                            onSearch={handleSearch}
                            placeholder={t("Search team assets...")}
                        />

                        <FilterDropDown
                            onChange={handleMediaFilterChange}
                            selectedFilter={mediaFilterValue}
                            filterOptions={mediaFilterOptions}
                        />
                        <FilterDropDown
                            onChange={handleTagFilterChange}
                            selectedFilter={tagFilterValue}
                            filterOptions={tagOptions}
                        />
                        <SortMenu
                            selectedOption={sort[0]?.field}
                            disabled={search.query.length > 0}
                            sortOptions={sortOptions}
                            onSetSort={handleSort}
                        />
                    </StyledSearchBarInnerContainer>
                )}
                {canUpgradeToManageSharedResources && (
                    <FlexBox center fillWidth padding={editable ? "40px 0px 0px 0px" : "0px 0px 0px 0px"}>
                        <Banner
                            headerText={t("Unlock Team Assets")}
                            bodyText={t("Upgrade to a Team plan to centralize approved brand assets.")}
                            buttonLabel={t("Upgrade")}
                            onButtonClick={() => ShowDialog(ChangePlanDialog,
                                {
                                    featuredPlan: WorkspacePlanTier.TEAM,
                                    analytics: { cta: "unlock_team_assets" }
                                })}
                        />
                    </FlexBox>
                )}
            </StyledSearchBarContainer>
            <StyledUIPaneResultsContainer editable={editable || canUpgradeToManageSharedResources}>
                {isLoadingInitial && <FetchingClickShield visible backgroundColor={editable ? "#eee" : "#fff"} />}
                {filteredAssets.length > 0 &&
                    <ThumbnailGridContainer>
                        <MultiSelectThumbnailGrid
                            items={filteredAssets}
                            responsiveColumns
                            colGap={24}
                            rowGap={24}
                            readOnly={!editable}
                            allowDragging={false}
                            allowMultiSelect={editable}
                            thumbnailClass={AssetThumbnail}
                            thumbnailProps={{ canUpgradeToManageSharedResources, onVisibleChange: handleThumbnailVisibilityChange }}
                            onItemSelected={selection => {
                                if (canUpgradeToManageSharedResources) {
                                    return;
                                }
                                if (addAssetCallback) {
                                    addAssetCallback(selection[0]);
                                    handleConfirm();
                                }
                            }}
                            onDoubleClick={canUpgradeToManageSharedResources ? undefined : ((asset: any) => handleEditSettings(asset))}
                            renderContextMenuItems={(selection: IWorkspaceResourceAsset[]) => renderContextMenu(selection)}
                            isLoadingMore={isLoadingMore}
                        />
                    </ThumbnailGridContainer>
                }
                {filteredAssets.length === 0 && (
                    (
                        !search.query &&
                        mediaFilterValue === "__all__" &&
                        tagFilterValue === "__all__"
                    )
                        ? <NoTeamAssetNotice />
                        : <NoMatchNotice />
                )}
            </StyledUIPaneResultsContainer>
        </UIPane>
    );
}

function TeamAssetsPaneWrapper(props: TeamAssetsPaneProps & WorkspaceControllerState & SharedAssetsPaneControllerState) {
    useEffect(() => {
        const filters: SharedAssetsPaneControllerState["filters"] = [
            {
                field: "resource.metadata.type",
                value: ["image", "logo"],
                operator: "in"
            },
        ];

        if (!props.editable) {
            filters.push({
                field: "resource.metadata.isPublished",
                value: true,
                operator: "eq"
            });
        }

        SharedAssetsPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.ASSET],
            search: { query: props.prefilledSearchQuery ?? "" },
            sort: [{ field: "resource.modifiedAt", direction: "desc" }],
            filters
        });

        return () => {
            SharedAssetsPaneController.reset();
        };
    }, [props.editable]);

    if (!SharedAssetsPaneController.initialized) {
        return <UIPane><FetchingClickShield visible backgroundColor="#eee" /></UIPane>;
    }

    return <TeamAssetsPane {...props} />;
}

export default WorkspaceController.withInitializedState(SharedAssetsPaneController.withState(TeamAssetsPaneWrapper));
